import { timestampDate } from '@bufbuild/protobuf/wkt'
import { AddOutlined, Check, Close } from '@mui/icons-material'
import { Avatar } from '@mui/material'
import {
    DataGrid,
    getGridSingleSelectOperators,
    GridColDef,
    GridPaginationModel,
    GridSortModel,
} from '@mui/x-data-grid'
import { createFileRoute, stripSearchParams } from '@tanstack/react-router'
import { isWithinInterval } from 'date-fns'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod/v4-mini'
import { ConfirmationDialog } from '@/components/ConfirmationDialog'
import { FabLink } from '@/components/FabLink'
import { AdminOverviewPagePendingComponent } from '@/features/admin/components/AdminOverviewPagePendingComponent'
import { CustomAdminDataGridToolbar } from '@/features/admin/components/CustomAdminDataGridToolbar'
import { assertUserIsAdminOrRedirect } from '@/features/admin/helper/assertUserIsAdminOrRedirect'
import { projectFieldNameAndSortByEnumMap } from '@/features/admin/projects/overview/helper/projectFieldNameAndSortByEnumMap'
import { projectsQueryFilters } from '@/features/admin/projects/overview/helper/projectsQueryFilters'
import { useDeleteProject } from '@/features/admin/projects/overview/mutations/useDeleteProject'
import { ProjectSortBy } from '@/generated/Enumerations/ProjectSortBy_pb'
import { ProjectDto } from '@/generated/Messages/Basic_pb'
import { UuidDto } from '@/generated/Messages/Common_pb'
import { debounce } from '@/helper/debounce'
import { stringifyId } from '@/helper/id'
import { useCreateCUDColumn } from '@/hooks/useCreateCUDColumn'
import { projectsQueryOptions, useProjects } from '@/queries/useProjects'
import {
    availablePageSizes,
    searchParamsPaginationDefaultValues,
    searchParamsPaginationSchema,
} from '@/schemas/searchParams/searchParamsPaginationSchema'
import {
    searchParamsSearchTextDefaultValues,
    searchParamsSearchTextSchema,
} from '@/schemas/searchParams/searchParamsSearchTextSchema'
import {
    searchParamsSortingDefaultValues,
    searchParamsSortingSchema,
} from '@/schemas/searchParams/searchParamsSortingSchema'

const seleniumPrefix = 'projects'

const useStaticColumns = (): GridColDef<ProjectDto>[] => {
    const { t } = useTranslation()

    return React.useMemo(
        () => [
            {
                field: 'displayColor',
                headerName: t('projects.color'),
                flex: 0.25,
                minWidth: 100,
                filterable: false,
                hideable: false,
                resizable: false,
                type: 'actions',
                renderCell: params => {
                    const color = params.row.displayColor
                    return (
                        <Avatar
                            style={{
                                backgroundColor: color,
                                width: 24,
                                height: 24,
                            }}
                        >
                            {' '}
                        </Avatar>
                    )
                },
            },
            {
                field: 'name',
                headerName: t('projects.name'),
                flex: 1,
                minWidth: 150,
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'description',
                headerName: t('projects.description'),
                flex: 1,
                minWidth: 200,
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'startAt',
                headerName: t('projects.startDate'),
                flex: 0.5,
                minWidth: 150,
                valueGetter: (_, row) =>
                    row.startAt ?
                        t('common.dateOnly.short', {
                            value: timestampDate(row.startAt),
                        })
                    :   '',
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'endAt',
                headerName: t('projects.endDate'),
                flex: 0.5,
                minWidth: 150,
                valueGetter: (_, row) =>
                    row.endAt ?
                        t('common.dateOnly.short', {
                            value: timestampDate(row.endAt),
                        })
                    :   '',
                filterable: false,
                hideable: false,
                resizable: false,
            },
            {
                field: 'valid',
                headerName: t('projects.valid'),
                flex: 0.5,
                minWidth: 100,
                valueGetter: (_, row) =>
                    row.startAt &&
                    row.endAt &&
                    isWithinInterval(new Date(), {
                        start: timestampDate(row.startAt),
                        end: timestampDate(row.endAt),
                    }),
                hideable: false,
                resizable: false,
                filterable: true,
                sortable: false,
                type: 'singleSelect',
                filterOperators: getGridSingleSelectOperators().filter(
                    operator => operator.value === 'is'
                ),
                valueOptions: [{ value: true, label: t('common.yes') }],
                renderCell: ({ value }) =>
                    typeof value === 'boolean' &&
                    (value ?
                        <Check fontSize={'small'} color={'disabled'} />
                    :   <Close fontSize={'small'} color={'disabled'} />),
                align: 'center',
            },
        ],
        [t]
    )
}

const RouteComponent: React.FC = () => {
    const {
        page,
        pageSize,
        sortByField,
        sortDirectionDesc,
        searchText,
        showOnlyCurrentlyValid,
    } = Route.useSearch()

    const navigate = Route.useNavigate()
    const { t } = useTranslation()
    const {
        data: { projects, pagination },
    } = useProjects(
        projectsQueryFilters(
            pageSize,
            page,
            sortByField,
            sortDirectionDesc,
            searchText,
            showOnlyCurrentlyValid
        )
    )

    const deleteProject = useDeleteProject()

    const [projectToDelete, setProjectToDelete] = React.useState<UuidDto>()

    const staticColumns = useStaticColumns()
    const cudColumn = useCreateCUDColumn<ProjectDto>({
        viewLinkProps: row => ({
            to: '/admin/projects/$id/view',
            params: { id: stringifyId(row.id) },
        }),
        editLinkProps: row => ({
            to: '/admin/projects/$id/edit',
            params: {
                id: stringifyId(row.id),
            },
            disabled: !!row.timestamps?.deletedAt,
        }),
        deleteProps: row => ({
            onClick: () => setProjectToDelete(row.id),
            disabled: !!row.timestamps?.deletedAt,
        }),
    })

    const columns = React.useMemo(
        () => [...staticColumns, cudColumn],
        [staticColumns, cudColumn]
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onChangeSearchText = React.useCallback(
        debounce(async (searchText: string) => {
            await navigate({
                search: prev => ({ ...prev, searchText, page: 0 }),
            })
        }),
        []
    )

    const paginationModel = React.useMemo(
        (): GridPaginationModel => ({ page, pageSize }),
        [page, pageSize]
    )
    const sortModel = React.useMemo(
        (): GridSortModel =>
            (
                sortByField !== ProjectSortBy.UNSPECIFIED &&
                projectFieldNameAndSortByEnumMap[sortByField]
            ) ?
                [
                    {
                        field: projectFieldNameAndSortByEnumMap[sortByField],
                        sort: sortDirectionDesc ? 'desc' : 'asc',
                    },
                ]
            :   [],
        [sortByField, sortDirectionDesc]
    )
    const filterModel = React.useMemo(
        () => ({
            items: [
                {
                    field: 'valid',
                    operator: 'is',
                    value:
                        !showOnlyCurrentlyValid ? undefined : (
                            showOnlyCurrentlyValid
                        ),
                },
            ],
        }),
        [showOnlyCurrentlyValid]
    )

    return (
        <div>
            <title>{t('projects.urlIndex')}</title>

            <DataGrid
                rows={projects}
                columns={columns}
                pageSizeOptions={availablePageSizes}
                disableRowSelectionOnClick={true}
                getRowId={row => stringifyId(row.id)}
                slots={{ toolbar: CustomAdminDataGridToolbar }}
                slotProps={{
                    toolbar: {
                        createButton: (
                            <FabLink
                                variant={'extended'}
                                color={'primary'}
                                disabled={false}
                                to={'/admin/projects/create'}
                                sx={{
                                    display: { xs: 'none', sm: 'inline-flex' },
                                }}
                                data-selenium={`${seleniumPrefix}-new`}
                            >
                                <AddOutlined /> {t('common.new')}
                            </FabLink>
                        ),
                        initialSearchText: searchText,
                        onChangeSearchText,
                    },
                }}
                paginationMode={'server'}
                sortingMode={'server'}
                filterMode={'server'}
                rowCount={pagination?.totalItemCount ?? 0}
                paginationModel={paginationModel}
                sortModel={sortModel}
                filterModel={filterModel}
                onPaginationModelChange={async model =>
                    await navigate({ search: prev => ({ ...prev, ...model }) })
                }
                onSortModelChange={async ([model]) => {
                    const castField = model?.field as
                        | keyof ProjectDto
                        | undefined
                    const field =
                        castField ?
                            projectFieldNameAndSortByEnumMap[castField]
                        :   undefined

                    await navigate({
                        search: prev => ({
                            ...prev,
                            page: 0,
                            sortByField: field,
                            sortDirectionDesc: model?.sort === 'desc',
                        }),
                    })
                }}
                onFilterModelChange={async model => {
                    const showOnlyCurrentlyValid = model.items.find(
                        i => i.field === 'valid'
                    )?.value as boolean | undefined

                    await navigate({
                        search: prev => ({
                            ...prev,
                            page: 0,
                            showOnlyCurrentlyValid:
                                showOnlyCurrentlyValid ?? false,
                        }),
                    })
                }}
                showToolbar={true}
            />
            <FabLink
                variant={'extended'}
                color={'primary'}
                disabled={false}
                to={'/admin/projects/create'}
                sx={theme => ({
                    display: { xs: 'inline-flex', sm: 'none' },
                    position: 'absolute',
                    bottom: theme.spacing(2),
                    right: theme.spacing(2),
                })}
                data-selenium={`${seleniumPrefix}-new`}
            >
                <AddOutlined />
                {t('common.new')}
            </FabLink>

            <ConfirmationDialog
                open={!!projectToDelete}
                onClickNo={() => setProjectToDelete(undefined)}
                onClickYes={async () => {
                    if (!projectToDelete) {
                        return
                    }

                    setProjectToDelete(undefined)
                    await deleteProject.mutateAsync(projectToDelete)
                }}
            />
        </div>
    )
}

const PendingComponent: React.FC = () => {
    const { page, pageSize, searchText } = Route.useSearch()
    const staticColumns = useStaticColumns()
    const { t } = useTranslation()
    const cudColumn = useCreateCUDColumn<ProjectDto>()

    const columns = React.useMemo(
        () => [...staticColumns, cudColumn],
        [staticColumns, cudColumn]
    )

    return (
        <AdminOverviewPagePendingComponent
            pageTitle={t('projects.urlIndex')}
            columns={columns}
            page={page}
            pageSize={pageSize}
            searchText={searchText}
        />
    )
}

export const Route = createFileRoute('/__base-layout/admin/projects/')({
    component: RouteComponent,
    validateSearch: z.object({
        ...searchParamsPaginationSchema.shape,
        ...searchParamsSortingSchema(ProjectSortBy, ProjectSortBy.UNSPECIFIED)
            .shape,
        ...searchParamsSearchTextSchema.shape,

        showOnlyCurrentlyValid: z._default(z.boolean(), true),
    }),
    search: {
        middlewares: [
            stripSearchParams({
                ...searchParamsPaginationDefaultValues,
                ...searchParamsSortingDefaultValues(ProjectSortBy.UNSPECIFIED),
                ...searchParamsSearchTextDefaultValues,

                showOnlyCurrentlyValid: true,
            }),
        ],
    },

    beforeLoad: ({
        search: {
            page,
            pageSize,
            sortByField,
            sortDirectionDesc,
            searchText,
            showOnlyCurrentlyValid,
        },
        context: { queryClient, grpcClients, user },
    }) => {
        assertUserIsAdminOrRedirect(user)

        return queryClient.ensureQueryData(
            projectsQueryOptions(
                grpcClients,
                projectsQueryFilters(
                    pageSize,
                    page,
                    sortByField,
                    sortDirectionDesc,
                    searchText,
                    showOnlyCurrentlyValid
                )
            )
        )
    },
    pendingMs: 2000,
    pendingComponent: PendingComponent,
})
